import { SONAR_MAX_RANGE_METERS, getSonarPulsePowerMw, getSonarScanCostMultiplier } from '../../shared/constants.js';
import type { ControlInput, GameSnapshot, RoomState } from '../../shared/types.js';
import type { AggregatedConnection, TileJsonNode } from '../../shared/maps/tileJson.js';
import { CaptainNavigation25DView } from '../rendering/CaptainNavigation25DView.js';
import type { NavGatewayEntry } from '../rendering/navSection.js';
import { t } from '../i18n.js';

type NodeEntry = { node: TileJsonNode; tileId: string; chunkRegion: string; biome: string; tempC: number; tileKind?: string };

type CreateNavigationRoomControllerOptions = {
  controls: HTMLDivElement | null;
  emitControl: (input: ControlInput) => void;
  getRevealedNodes: () => Set<string>;
  onWaypointChange?: (pos: { worldX: number; worldZ: number; depth?: number } | null) => void;
};

/** Battery charge (%) a max-range sub-surface scan uses; the server deducts this straight from the charge. */
export const SUBSURFACE_SCAN_COST = getSonarPulsePowerMw(SONAR_MAX_RANGE_METERS) * getSonarScanCostMultiplier('subsurface');

export const createNavigationRoomController = ({
  controls,
  emitControl,
  getRevealedNodes,
  onWaypointChange
}: CreateNavigationRoomControllerOptions) => {
  let mapView: CaptainNavigation25DView | null = null;
  let navigationViewportLayoutCleanup: (() => void) | null = null;
  let waypointButton: HTMLButtonElement | null = null;
  let hasWaypoint = false;
  let scanButton: HTMLButtonElement | null = null;

  /** Keep the scan button's availability in step with the snapshot. */
  const updateScanUi = (snapshot: GameSnapshot | null): void => {
    const charge = snapshot?.submarine.batteryCharge ?? 0;
    if (scanButton) {
      const affordable = charge >= SUBSURFACE_SCAN_COST;
      scanButton.disabled = !snapshot || !affordable;
      // Phones never show tooltips, so put the reason on the button itself.
      scanButton.textContent = affordable ? 'Sub-surface scan' : `Sub-surface scan — needs ${Math.round(SUBSURFACE_SCAN_COST)}% battery`;
      scanButton.title = affordable ? 'Run a sub-surface scan at maximum range.' : `Needs ${Math.round(SUBSURFACE_SCAN_COST)}% battery.`;
    }
  };

  const updateWaypointButton = (): void => {
    if (!waypointButton) return;
    waypointButton.textContent = hasWaypoint ? t('nav_clear_waypoint') : t('nav_set_waypoint');
    waypointButton.style.background = hasWaypoint ? 'rgba(220,60,60,0.25)' : '';
    waypointButton.style.borderColor = hasWaypoint ? '#dc3c3c' : '';
    waypointButton.style.color = hasWaypoint ? '#ff8888' : '';
  };

  // Cached tile data so the view can be re-hydrated when render() recreates it
  let cachedNodeEntries: NodeEntry[] = [];
  let cachedConnections: AggregatedConnection[] = [];
  let cachedGateways: NavGatewayEntry[] = [];

  /** Push the current revealed-node set into the map view. */
  const syncRevealedNodes = (): void => {
    mapView?.setRevealedNodes(getRevealedNodes());
  };

  /** Update tile node + connection data in the map view. */
  const setTileData = (nodeEntries: NodeEntry[], connections: AggregatedConnection[], gateways: NavGatewayEntry[] = []): void => {
    cachedNodeEntries = nodeEntries;
    cachedConnections = connections;
    cachedGateways = gateways;
    mapView?.setTileData(nodeEntries, connections, gateways);
  };

  const setRoomState = (roomState: RoomState): void => {
    mapView?.setRoomState(roomState);
  };

  const setSnapshot = (snapshot: GameSnapshot): void => {
    mapView?.setSnapshot(snapshot);
    updateScanUi(snapshot);
    // The waypoint is mirrored from the server, so keep the button label in step with it.
    if (mapView && mapView.hasWaypoint() !== hasWaypoint) {
      hasWaypoint = mapView.hasWaypoint();
      updateWaypointButton();
    }
  };

  const render = (lastRoomState: RoomState | null, lastSnapshot: GameSnapshot | null): void => {
    if (!controls) {
      return;
    }

    const navCard = document.createElement('section');
    navCard.className = 'control-card ctrl-room-main-card';

    const heading = document.createElement('h3');
    heading.textContent = t('nav_heading');
    const description = document.createElement('p');
    description.className = 'control-card-description';
    description.textContent = t('nav_description');

    controls.classList.add('navigation-role-layout');
    const navViewport = document.createElement('div');
    navViewport.className = 'captain-navigation-viewport';
    navViewport.style.width = '100%';
    navViewport.style.touchAction = 'none';
    navViewport.style.position = 'relative';
    const headerRow = document.createElement('div');
    headerRow.className = 'nav-card-header';
    const titleBlock = document.createElement('div');
    titleBlock.append(heading, description);

    headerRow.append(titleBlock);
    navCard.append(headerRow, navViewport);
    controls.append(navCard);

    navigationViewportLayoutCleanup?.();
    const syncNavigationViewportSize = (): void => {
      const navCardRect = navCard.getBoundingClientRect();
      const controlsRect = controls.getBoundingClientRect();
      const viewportHeight = window.visualViewport?.height ?? window.innerHeight;
      const availableWithinPanel = controlsRect.bottom - navCardRect.top - 12;
      const availableInViewport = viewportHeight - navCardRect.top - 12;
      const nextHeight = Math.max(280, Math.floor(Math.max(availableWithinPanel, availableInViewport)));
      navViewport.style.height = `${nextHeight}px`;
    };

    const onWindowResize = (): void => syncNavigationViewportSize();
    window.addEventListener('resize', onWindowResize, { passive: true });
    window.visualViewport?.addEventListener('resize', onWindowResize);
    navigationViewportLayoutCleanup = () => {
      window.removeEventListener('resize', onWindowResize);
      window.visualViewport?.removeEventListener('resize', onWindowResize);
    };
    syncNavigationViewportSize();

    // Save pan/zoom so re-entering the room doesn't reset the view
    const savedViewState = mapView?.getViewState() ?? null;
    mapView?.dispose();
    mapView = new CaptainNavigation25DView(navViewport);

    // Re-hydrate with cached tile data (may have been loaded before this render call)
    if (cachedNodeEntries.length > 0) {
      mapView.setTileData(cachedNodeEntries, cachedConnections, cachedGateways);
    }

    // Restore pan/zoom if we had a previous view (otherwise keep the default zoom)
    if (savedViewState) {
      mapView.restoreViewState(savedViewState);
    }

    mapView.onWaypointChange = (pos) => {
      hasWaypoint = pos !== null;
      updateWaypointButton();
      onWaypointChange?.(pos);
    };

    const rightAuxZone =
      document.querySelector<HTMLDivElement>('#ctrlZoneRightAux') ??
      document.querySelector<HTMLDivElement>('#ctrlZoneRight');
    if (rightAuxZone) {
      rightAuxZone.innerHTML = '';
      rightAuxZone.style.display = 'grid';
      rightAuxZone.style.minHeight = '5rem';
      rightAuxZone.style.gap = '0.5rem';
      rightAuxZone.style.alignItems = 'stretch';

      scanButton = document.createElement('button');
      scanButton.type = 'button';
      scanButton.className = 'primary nav-scan-button';
      scanButton.textContent = 'Sub-surface scan';
      scanButton.addEventListener('click', () => {
        // Always fired at maximum range; the server enforces this too.
        emitControl({ action: 'sonar_ping', value: SONAR_MAX_RANGE_METERS, scanType: 'subsurface' });
      });
      rightAuxZone.appendChild(scanButton);

      // Recentre has its own button: it used to be bound to the whole zone, so pressing the
      // waypoint button also reset the view.
      const recenterButton = document.createElement('button');
      recenterButton.type = 'button';
      recenterButton.className = 'secondary';
      recenterButton.textContent = 'Recentre';
      recenterButton.addEventListener('click', () => { mapView?.recenter(); });
      rightAuxZone.appendChild(recenterButton);

      waypointButton = document.createElement('button');
      waypointButton.className = 'ctrl-button';
      updateWaypointButton();
      waypointButton.addEventListener('click', () => { mapView?.toggleWaypoint(); });
      rightAuxZone.appendChild(waypointButton);
    }

    if (lastRoomState) {
      mapView.setRoomState(lastRoomState);
    }
    if (lastSnapshot) {
      mapView.setSnapshot(lastSnapshot);
    }
    updateScanUi(lastSnapshot);

    // Push current revealed nodes so history (including restored saves) is
    // displayed immediately when the nav room is switched into.
    syncRevealedNodes();
  };

  return {
    render,
    setTileData,
    syncRevealedNodes,
    setRoomState,
    setSnapshot
  };
};
